import React, { useState } from "react"
import PropTypes from "prop-types"
import { CdnIntegrationsList } from "@modules/cdn/shared/components/CdnIntegrationsList"
import { CdnIntegrationModal } from "@modules/cdn/shared/components/CdnIntegrationModal"
import { CdnDeleteModal } from "@modules/cdn/shared/components/CdnDeleteModal"
import {
  SettingsBlock,
  SettingsBlockContent,
  SettingsCardSection,
} from "@modules/site/settings"
import { useSupportedCdnIntegrationsQuery } from "@modules/cdn/shared/queries.generated"
import { useSitePermissionsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { Domains } from "./Domains"
import { ConnectBlockUp } from "./IntegrationTargetSection.parts"
import { useHostingIntegrationsUsage } from "@modules/organization/shared/hooks/useHostingIntegrationsUsage"
import { FeatureUsageInfo } from "@modules/site/shared/components/FeatureUsageInfo"
import { SiteSettingsSection, SiteSettingsSectionLabel } from "../constants"
import { GatsbyHostingSection } from "./GatsbyHostingSection"

const propTypes = {
  siteId: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
}

const integrationTargetWrapperCss = {
  minHeight: `30rem`,
  marginBottom: `5rem`,
}

export function IntegrationTargetSection({ siteId, siteName, organizationId }) {
  const [selectedCdnToEdit, setSelectedCdnToEdit] = useState()
  const [selectedCdnToRemove, setSelectedCdnToRemove] = useState()
  const [isBlockUpOpen, setIsBlockupOpen] = React.useState(false)

  const {
    data: cdnIntegrationsData,
    loading,
    error,
    refetch,
  } = useSupportedCdnIntegrationsQuery({
    variables: { siteId },
  })
  const { data: permissionsData } = useSitePermissionsFieldsFragment(siteId)

  const hostingIntegrationsUsage = useHostingIntegrationsUsage(organizationId)

  const handleSelect = val => {
    if (
      !hostingIntegrationsUsage.isUnlimited &&
      hostingIntegrationsUsage.isExhausted &&
      !val?.connected
    ) {
      setIsBlockupOpen(true)
    } else {
      setSelectedCdnToEdit(val)
    }
  }

  const handleConnect = () => {
    refetch()
    hostingIntegrationsUsage.recalculate()
  }

  if (loading) {
    return <div data-testid="cdn-list-loading">Loading</div>
  }

  const cdnIntegrations = cdnIntegrationsData?.supportedCDNIntegrations ?? []

  return (
    <React.Fragment>
      <GatsbyHostingSection
        siteId={siteId}
        siteName={siteName}
        onEnabledSuccess={hostingIntegrationsUsage.recalculate}
      />

      <Domains siteId={siteId} organizationId={organizationId} />

      <SettingsBlock
        id={SiteSettingsSection.HostingIntegrations}
        data-cy="deploy-targets-card"
        title={
          SiteSettingsSectionLabel[SiteSettingsSection.HostingIntegrations]
        }
        note={
          !hostingIntegrationsUsage.isUnlimited && (
            <FeatureUsageInfo
              organizationId={organizationId}
              isRecalculating={hostingIntegrationsUsage.isRecalculating}
              message={hostingIntegrationsUsage.message}
            />
          )
        }
        description="Gatsby Cloud can automatically deploy each site build to a CDN host of your choice—just connect and you are good to go!"
        css={integrationTargetWrapperCss}
      >
        <SettingsBlockContent>
          {error ? (
            <SettingsCardSection applyPaddingTop={false}>
              <ErrorAlert>{error.message}</ErrorAlert>
            </SettingsCardSection>
          ) : (
            <CdnIntegrationsList
              siteId={siteId}
              integrations={cdnIntegrations}
              onSelect={handleSelect}
              onDelete={setSelectedCdnToRemove}
              allowToEdit={permissionsData?.permissions?.sites?.edit}
            />
          )}
        </SettingsBlockContent>
      </SettingsBlock>

      {selectedCdnToEdit && (
        <CdnIntegrationModal
          siteId={siteId}
          siteName={siteName}
          cdn={selectedCdnToEdit}
          onClose={() => setSelectedCdnToEdit(undefined)}
          onSuccess={handleConnect}
        />
      )}

      {selectedCdnToRemove && (
        <CdnDeleteModal
          cdn={selectedCdnToRemove}
          siteName={siteName}
          siteId={siteId}
          organizationId={organizationId}
          onClose={() => setSelectedCdnToRemove(undefined)}
          onSuccess={hostingIntegrationsUsage.recalculate}
        />
      )}

      <ConnectBlockUp
        organizationId={organizationId}
        isOpen={isBlockUpOpen}
        onDissmis={() => setIsBlockupOpen(false)}
        quantity={hostingIntegrationsUsage.all}
      />
    </React.Fragment>
  )
}

IntegrationTargetSection.propTypes = propTypes
